import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { bodyTooltip, chartTitle, type OptionContext } from './shared.ts';

type PieConfig = Extract<ChartConfig, { type: 'pie' }>;

function stanceFill(
  stance: 'praise' | 'friction' | undefined,
  dark: boolean,
  fallback: string
): string {
  if (stance === 'praise') return dark ? '#c47a3a' : '#b86f32';
  if (stance === 'friction') return dark ? '#8b4554' : '#7a3d4a';
  return fallback;
}

export function buildPieOption(
  config: PieConfig,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor, dark } = theme;
  const total = config.values.reduce((a, b) => a + b, 0);
  const n = config.labels.length;
  const manySlices = n > 6;
  const rose = config.roseType === 'radius' || config.roseType === 'area';
  const showLegend = config.showLegend !== false && !rose;
  const showOutsideLabels =
    config.showLabels === false ? false : rose ? n > 0 : n <= 6;
  const ringOnly = !rose && !showLegend && config.showLabels === false;
  const defaultPiePalette = [
    colors.primary,
    colors.positive,
    colors.negative,
    '#6366f1',
    '#f59e0b',
    '#06b6d4',
    '#a855f7',
    '#ec4899',
    '#14b8a6',
    '#84cc16',
    '#f97316',
    '#3b82f6',
    '#eab308',
  ];
  const piePalette =
    config.seriesColors && config.seriesColors.length > 0
      ? config.seriesColors
      : defaultPiePalette;
  const legend = showLegend
    ? n > 12
      ? {
          type: 'scroll' as const,
          orient: 'horizontal' as const,
          bottom: 6,
          left: 'center' as const,
          width: '92%',
          itemGap: 12,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: textColor, fontSize: 10 },
          pageIconColor: textColor,
          pageTextStyle: { color: textColor },
          pageButtonItemGap: 8,
        }
      : {
          type: 'plain' as const,
          bottom: 6,
          left: 'center' as const,
          width: manySlices ? '90%' : '96%',
          itemGap: manySlices ? 12 : 16,
          itemWidth: 10,
          itemHeight: 10,
          padding: [0, 4, 4, 4] as [number, number, number, number],
          textStyle: { color: textColor, fontSize: manySlices ? 9 : 10 },
        }
    : undefined;

  const roseRadius = manySlices ? ['8%', '62%'] : ['8%', '68%'];
  const donutRadius = ringOnly
    ? (['40%', '72%'] as [string, string])
    : manySlices
      ? (['30%', '52%'] as [string, string])
      : (['38%', '65%'] as [string, string]);

  const pieCenter: [string, string] = rose
    ? ['50%', '50%']
    : ringOnly
      ? ['50%', '50%']
      : [
          '50%',
          config.title
            ? manySlices
              ? '41%'
              : '46%'
            : manySlices
              ? '39%'
              : '44%',
        ];

  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    tooltip: bodyTooltip({
      trigger: 'item',
      formatter: (p) => {
        const item = p as { name: string; value: number; percent: number };
        return `${item.name}: ${item.value} (${item.percent}%)`;
      },
    }),
    color: piePalette,
    ...(legend ? { legend } : { legend: { show: false } }),
    series: [
      {
        type: 'pie' as const,
        ...(rose ? { roseType: config.roseType } : {}),
        radius: rose ? roseRadius : donutRadius,
        center: pieCenter,
        avoidLabelOverlap: true,
        minShowLabelAngle: rose ? 0 : manySlices ? 10 : 4,
        label: {
          show: total > 0 && showOutsideLabels,
          position: 'outside' as const,
          color: textColor,
          fontSize: rose ? (manySlices ? 10 : 11) : manySlices ? 9 : 10,
          formatter: rose ? '{b}' : '{b}: {d}%',
        },
        labelLine: {
          show: showOutsideLabels,
          length: rose ? (manySlices ? 12 : 16) : manySlices ? 10 : 14,
          length2: rose ? (manySlices ? 10 : 14) : manySlices ? 8 : 12,
          lineStyle: { color: axisLineColor },
          smooth: 0.2,
        },
        emphasis: {
          scale: false,
          scaleSize: 4,
        },
        data: config.labels.map((label, i) => {
          const stance = config.stances?.[i];
          const value = config.values[i] ?? 0;
          const paletteColor =
            piePalette[i % piePalette.length] ?? colors.primary;
          if (!rose && stance === undefined && !config.seriesColors) {
            return { name: label, value };
          }
          const fill =
            stance !== undefined
              ? stanceFill(stance, dark, paletteColor)
              : paletteColor;
          return {
            name: label,
            value,
            itemStyle: {
              color: fill,
              borderColor: dark
                ? 'rgba(15, 15, 20, 0.92)'
                : 'rgba(255, 255, 255, 0.95)',
              borderWidth: rose ? 2 : 0,
            },
          };
        }),
      },
    ],
  };
}

export function buildFunnelOption(
  config: Extract<ChartConfig, { type: 'funnel' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const data = config.labels.map((name, i) => ({
    name,
    value: config.values[i] ?? 0,
  }));
  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    tooltip: bodyTooltip({
      trigger: 'item',
      formatter: (p) => {
        const item = p as { name: string; value: number; percent: number };
        return `${item.name}: ${item.value} (${item.percent}%)`;
      },
    }),
    color: [
      colors.primary,
      colors.positive,
      colors.negative,
      '#6366f1',
      '#f59e0b',
      '#06b6d4',
    ],
    series: [
      {
        type: 'funnel',
        sort: 'descending',
        left: '8%',
        top: config.title ? 40 : 24,
        width: '84%',
        height: config.title ? '68%' : '72%',
        minSize: '0%',
        maxSize: '100%',
        label: {
          show: true,
          color: textColor,
          fontSize: 10,
          formatter: '{b}: {c}',
        },
        itemStyle: { borderColor: axisLineColor, borderWidth: 1 },
        data,
      },
    ],
  };
}
